import { Card } from 'components/ui/card';
import { useGlobalSearch } from 'hooks/useGlobalSearch';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { useLazyGlobalSearchQuery } from 'store/api/globalSearch';
import { GLOBAL_SEARCH_MIN_QUERY_LENGTH, GlobalSearchRow } from 'types/globalSearch';
import { recentIdsHint } from 'utils/recentlyViewed';
import { FacetRail, facetValueLabel } from './FacetRail';
import { NarrowedBy, NarrowedByChip, ResultCard } from './ResultCard';
import { NothingFound } from './NothingFound';
import {
    FACET_DIMENSIONS,
    parseSearchFilters,
    QUERY_PARAM,
    selectedFacetValues,
    toggleFacetValue,
} from './searchFilters';

/** The pages fetched beyond the first, and where the next one starts. */
interface PagedRows {
    /** The query and facets these rows answer. A different one throws them away. */
    key: string;
    rows: GlobalSearchRow[];
    /** `undefined` until a page has been fetched here, after which it is that page's cursor. */
    nextCursor: number | null | undefined;
}

/**
 * The full results page.
 *
 * The URL is the whole of its state — the query and every facet selection — so a narrowed list can
 * be shared, reloaded and walked back through with the browser's own buttons, and the search box in
 * the chrome keeps showing what the page is answering.
 *
 * Paging is by cursor rather than by page index: the backend ranks the match set once and the
 * cursor is a position in it, so the accumulated rows are kept here and reset whenever the URL asks
 * a different question. The recently-viewed ranking hint goes out with every page for the same
 * reason — a later page ranked without it would not continue the list the earlier ones started.
 */
export function SearchResultsPage() {
    const intl = useIntl();
    const [searchParams, setSearchParams] = useSearchParams();
    const query = searchParams.get(QUERY_PARAM) ?? '';
    const filters = useMemo(() => parseSearchFilters(searchParams), [searchParams]);

    // No debounce: nothing is being typed here. The query arrives whole, from the URL.
    const { data, isLoading, isError, isTimedOut, isTooShort, searchedQuery } = useGlobalSearch({
        query,
        mode: 'RESULTS',
        filters,
        debounceMs: 0,
    });

    const pageKey = searchParams.toString();
    const [paged, setPaged] = useState<PagedRows>({ key: pageKey, rows: [], nextCursor: undefined });
    if (paged.key !== pageKey) {
        // Adjusted during render rather than in an effect: the rows below must never be drawn under
        // a heading that has already moved on to another question.
        setPaged({ key: pageKey, rows: [], nextCursor: undefined });
    }

    const [fetchPage, { isFetching: isLoadingMore }] = useLazyGlobalSearchQuery();
    const firstPageRows = data?.rows ?? [];
    const rows = paged.rows.length > 0 ? [...firstPageRows, ...paged.rows] : firstPageRows;
    const settledCursor = paged.nextCursor === undefined ? (data?.nextCursor ?? null) : paged.nextCursor;
    /*
     * Paging stops while the answer in hand is still the previous question's. `key` above only says
     * that nothing arrives under a heading that has moved on — it cannot say that a request LEAVING
     * now belongs here, and until the query settles `searchedQuery`, `data` and the cursor all still
     * describe what was asked before. A page fetched in that window would continue the previous
     * ranking and land under the current key, which is how one search's rows end up in another's.
     */
    const nextCursor = isLoading ? null : settledCursor;

    const loadMore = useCallback(async () => {
        if (nextCursor === null) {
            return;
        }
        try {
            const page = await fetchPage({
                ...filters,
                q: searchedQuery,
                mode: 'RESULTS',
                cursor: nextCursor,
                recentIds: recentIdsHint(),
            }).unwrap();
            setPaged((current) =>
                current.key !== pageKey
                    ? current
                    : { key: current.key, rows: [...current.rows, ...page.rows], nextCursor: page.nextCursor }
            );
        } catch {
            // The rows already loaded stay; the sentinel re-arms when the user scrolls away and back.
        }
    }, [fetchPage, filters, nextCursor, pageKey, searchedQuery]);

    const facets = data?.facets ?? [];

    const applySearch = (next: URLSearchParams) => setSearchParams(next);

    const narrowedBy: NarrowedByChip[] = FACET_DIMENSIONS.flatMap((dimension) =>
        selectedFacetValues(searchParams, dimension).map((value) => ({
            id: `${dimension}:${value}`,
            label: facetValueLabel(intl, dimension, value, facets),
            onRemove: () => applySearch(toggleFacetValue(searchParams, dimension, value)),
        }))
    );

    const searchFor = (term: string) => {
        const next = new URLSearchParams(searchParams);
        next.set(QUERY_PARAM, term);
        applySearch(next);
    };

    if (isTooShort || isError) {
        return (
            <div className="fullhd:p-6 p-3">
                <Card className="text-grey-500 rounded-[20px] px-6 py-14 text-center text-sm">
                    {isError
                        ? intl.formatMessage({ id: isTimedOut ? 'global-search.timed-out' : 'global-search.error' })
                        : intl.formatMessage(
                              { id: 'global-search.min-length-hint' },
                              { count: GLOBAL_SEARCH_MIN_QUERY_LENGTH }
                          )}
                </Card>
            </div>
        );
    }

    if (!isLoading && rows.length === 0) {
        return (
            <div className="fullhd:p-6 p-3">
                {/*
                 * The chips come first here: when a narrowing is what emptied the list, taking one
                 * off is the answer, and the rail that would have offered it is gone with the
                 * facets. They are read from the URL, so they survive a response with nothing in it.
                 */}
                <div className="w-full max-w-[720px]">
                    <NarrowedBy chips={narrowedBy} />
                </div>
                <NothingFound query={searchedQuery} didYouMean={data?.didYouMean ?? null} onDidYouMean={searchFor} />
            </div>
        );
    }

    // `data-peek="off"`: each row already draws what the card would show.
    return (
        <div
            data-peek="off"
            className="fullhd:p-6 new:gap-(--card-gap) flex flex-col gap-3 p-3 sm:flex-row sm:items-start"
        >
            <FacetRail facets={facets} params={searchParams} onChange={applySearch} />
            <ResultCard
                query={searchedQuery}
                total={data?.total ?? 0}
                capped={data?.capped ?? false}
                didYouMean={data?.didYouMean ?? null}
                onDidYouMean={searchFor}
                narrowedBy={narrowedBy}
                rows={rows}
                isLoading={isLoading && rows.length === 0}
                isLoadingMore={isLoadingMore}
                hasNextPage={nextCursor !== null}
                onLoadMore={loadMore}
            />
        </div>
    );
}

export default SearchResultsPage;
